"use client";

import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || "";
// Managed reverse proxy (PostHog → Settings → Managed reverse proxy): ad blockers drop
// *.posthog.com, which hid those users' events and replays.
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://p.insuracentral.com";

let initialized = false;

function initPostHog() {
  if (initialized || !POSTHOG_KEY || typeof window === "undefined") return;
  // ── First-party analytics consent (native only) ──
  // ATT is gone: we never did cross-app tracking, so Apple's framework didn't
  // apply — gating analytics behind it silently killed tracking whenever iOS
  // auto-denied (simulators, Settings toggle). Instead the app asks its own
  // consent question after login (ensureAnalyticsConsent) and we honor THAT:
  // denied = no analytics, ever; unanswered = wait (retried via the
  // analytics-consent-resolved event). Web is unaffected.
  // Migration: an install that answered the old ATT prompt "authorized" counts
  // as granted until the new question stamps analytics_consent.
  try {
    const capNative = (window as any).Capacitor?.isNativePlatform?.();
    if (capNative) {
      const consent = localStorage.getItem("analytics_consent");
      const legacyAtt = localStorage.getItem("att_status");
      const granted = consent === "granted" || (consent === null && legacyAtt === "authorized");
      if (!granted) return; // denied, or not asked yet — re-attempted on consent event
    }
  } catch { /* storage unavailable — treat as no consent on native */ }
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: "https://us.posthog.com", // links into PostHog (toolbar, replay URLs) can't use the proxy
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,

    // DO NOT let PostHog wrap window.fetch / XHR. Its performance+network capture
    // monkey-patches fetch to time requests; when PostHog is blocked by an
    // ad-blocker / privacy browser (Brave, uBlock — very common on agents'
    // personal devices) that wrapper can end up in a broken state and throw
    // "TypeError: Failed to fetch" for the APP's own requests (login,
    // /auth/profile). Users then couldn't log in on their own devices while a
    // clean office machine worked. Turning this off means PostHog never touches
    // the app's network layer — analytics + session recording still work, but
    // they can never break auth. (2026-07-24 login-outage hardening.)
    capture_performance: false,

    // Session recording — masks all inputs so passwords/tokens never leak.
    // Explicitly do NOT capture request/response bodies or headers (no fetch
    // instrumentation of auth calls).
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
      // Cloudflare Turnstile rewrites this hidden token every ~5 min. Recorded, that
      // reads as typing, so an idle sign-in tab never went idle (24 h replays).
      blockSelector: 'input[name="cf-turnstile-response"]',
      recordBody: false,
      recordHeaders: false,
    },

    // Surveys ENABLED for data only — agents still never see PostHog's UI.
    // RULE: in the PostHog dashboard, ONLY create surveys with type "API"
    // (never "Popover"). API surveys render nothing by themselves; our
    // branded <InsuraSurvey /> component fetches + renders them in CRM
    // styling and reports responses back. A Popover-type survey created by
    // mistake WOULD show PostHog's own widget — don't create those.
    disable_surveys: false,

    // Don't load any PostHog site apps (toolbar, heatmaps widget, etc.)
    opt_in_site_apps: false,

    // Don't load or show the in-app toolbar
    advanced_disable_toolbar_metrics: true,

    // Persist across sessions
    persistence: "localStorage+cookie",

    // Drop Safari setSinkId NotAllowedError from being captured as exceptions.
    // Twilio Voice SDK internally calls setSinkId() which Safari blocks without
    // a user gesture. These are harmless — audio still works via system default.
    before_send: (event) => {
      if (!event) return null;
      if (event.event === '$exception') {
        const props = event.properties || {};

        // Check top-level props (older PostHog format)
        const msg = props.$exception_message || '';
        const type = props.$exception_type || '';
        if (type === 'DOMException' && msg.includes('user gesture')) {
          return null;
        }

        // Check $exception_list (PostHog v2+ format — setSinkId errors land here)
        const exList = props.$exception_list || [];
        for (const ex of exList) {
          if (ex.type === 'DOMException' && ex.value?.includes('user gesture')) {
            return null;
          }
        }

        // Also drop "Non-Error promise rejection" with undefined value
        if (msg === 'Non-Error promise rejection captured with value: undefined') {
          return null;
        }
      }
      return event;
    },

    // Only log in dev
    loaded: () => {
      if (process.env.NODE_ENV === "development") {
        console.log("[PostHog] Initialized");
      }
    },
  });
  initialized = true;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const identified = useRef(false);
  // Bumped when native ATT consent resolves mid-session, so init is retried
  // and the identify effect re-runs. Without this, tapping "Allow" did nothing
  // until the NEXT cold launch — and even then the user stayed anonymous
  // because identify had already run before init.
  const [initEpoch, setInitEpoch] = useState(0);

  // Initialize PostHog once (re-attempted when ATT consent resolves)
  useEffect(() => {
    initPostHog();
  }, [initEpoch]);

  useEffect(() => {
    const onConsent = () => {
      initPostHog();
      setInitEpoch((n) => n + 1);
    };
    window.addEventListener("analytics-consent-resolved", onConsent);
    return () => window.removeEventListener("analytics-consent-resolved", onConsent);
  }, []);

  // Identify user when they log in, reset when they log out
  useEffect(() => {
    if (!POSTHOG_KEY || !initialized) return;

    if (!user?.user?.id) {
      if (identified.current) {
        posthog.reset();
        identified.current = false;
      }
      return;
    }

    posthog.identify(user.user.id, {
      email: user.user.email || "",
      name: user.user.name || "",
      phone: user.user.contactPhone || user.user.phone || "",
      accountType: user.user.accountType || "",
      accountStatus: user.user.accountStatus || "",
      userType: user.user.userType || "",
      isTrialActive: user.user.isTrialActive || false,
      isTestUser: user.user.isTestUser || false,
      mfaEnabled: user.user.mfaEnabled || false,
      agencyId: user.currentAgency?.agency?.id || "",
      agencyName: user.currentAgency?.agency?.name || "",
      agencyRole: user.currentAgency?.role || "",
      lastLoginIp: user.user.lastLoginIp || "",
    });
    identified.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user?.id, user?.user?.email, user?.currentAgency?.agency?.id, initEpoch]);

  // Renders nothing — completely invisible
  return <>{children}</>;
}

// Helper for manual event tracking anywhere in the app
export function trackEvent(eventName: string, properties?: Record<string, any>) {
  if (POSTHOG_KEY && initialized && typeof window !== "undefined") {
    posthog.capture(eventName, properties);
  }
}
